import { useState, useMemo } from 'react';
import { format, addMonths, startOfMonth, endOfMonth, isSameMonth, isSameDay, isBefore, startOfToday, eachDayOfInterval, differenceInCalendarDays, isWithinInterval } from 'date-fns';
import { ChevronLeft, ChevronRight, Clock, Info, Package, MessageSquare, CalendarRange, Users, Car } from 'lucide-react';
import { useVendorAvailability, useCreateBooking, useVendorPackages } from '../../hooks/useVendorAvailability';
import { WeddingVendor, WeddingPackage } from '../../types';
import toast from 'react-hot-toast';

interface BookingCalendarProps {
    vendor: WeddingVendor;
}

type SlotType = 'full_day' | 'morning' | 'evening';

export default function BookingCalendar({ vendor }: BookingCalendarProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date | null } | null>(null);
    const [bookingType, setBookingType] = useState<SlotType>('full_day');
    const [selectedPackageId, setSelectedPackageId] = useState<string>('');
    const [userNote, setUserNote] = useState('');
    const [guestCount, setGuestCount] = useState<number>(100);

    // Availability Hook
    const { data: bookings = [] } = useVendorAvailability(vendor.id, currentMonth);
    const { data: packages = [] } = useVendorPackages(vendor.id);
    const createBooking = useCreateBooking();

    // Helpers
    const isHalfDayEnabled = vendor.price_half_day && vendor.price_half_day > 0;
    const capacity = vendor.capacity || 1;
    const selectedPackage = packages.find(p => p.id === selectedPackageId);

    // Check specific day availability
    const getDayAvailability = (date: Date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayBookings = bookings.filter(b => b.booking_date === dateStr);

        // Capacity Check Logic
        // If capacity > 1, we just check count strictly for now (simple model).
        // If capacity == 1, we use slot logic.

        if (capacity > 1) {
            const confirmedCount = dayBookings.length;
            const isAvailable = confirmedCount < capacity;
            return { full: isAvailable, morning: isAvailable, evening: isAvailable, remaining: capacity - confirmedCount };
        }

        // Logic for Capacity = 1 (Exclusive)
        const isFullDayBooked = dayBookings.some(b => b.booking_type === 'full_day');
        const isMorningBooked = dayBookings.some(b => b.booking_type === 'morning');
        const isEveningBooked = dayBookings.some(b => b.booking_type === 'evening');

        if (isFullDayBooked) return { full: false, morning: false, evening: false, remaining: 0 };
        if (isMorningBooked && isEveningBooked) return { full: false, morning: false, evening: false, remaining: 0 };

        return {
            full: !isMorningBooked && !isEveningBooked,
            morning: !isMorningBooked,
            evening: !isEveningBooked,
            remaining: 0
        };
    };

    // Calculate Price
    const calculation = useMemo(() => {
        if (!dateRange?.start) return { total: 0, days: 0, perDay: 0 };

        let pricePerUnit = bookingType === 'full_day' ? (vendor.price_per_day || 0) : (vendor.price_half_day || 0);

        // Package Logic
        if (selectedPackage) {
            pricePerUnit = selectedPackage.price;
        }

        // Per Guest Logic
        if (selectedPackage?.is_per_guest) {
            pricePerUnit = pricePerUnit * guestCount;
        }

        if (dateRange.start && !dateRange.end) {
            return { total: pricePerUnit, days: 1, perDay: pricePerUnit };
        }

        if (dateRange.start && dateRange.end) {
            const days = differenceInCalendarDays(dateRange.end, dateRange.start) + 1;
            return { total: pricePerUnit * days, days, perDay: pricePerUnit };
        }

        return { total: 0, days: 0, perDay: 0 };
    }, [bookingType, dateRange, vendor, selectedPackage, guestCount]);

    const handleDateClick = (date: Date) => {
        if (isBefore(date, startOfToday())) return;
        const avail = getDayAvailability(date);

        // Availability Check
        if (!avail.full && !avail.morning && !avail.evening) return;

        // 1. Start new selection
        if (!dateRange || (dateRange.start && dateRange.end)) {
            setDateRange({ start: date, end: null });
            setSelectedPackageId('');
            setUserNote('');
            setGuestCount(100);

            if (avail.full) setBookingType('full_day');
            else if (avail.morning) setBookingType('morning');
            else if (avail.evening) setBookingType('evening');

            return;
        }

        // 2. Complete Range (Capacity check for all days)
        if (dateRange.start && !dateRange.end) {
            if (isSameDay(date, dateRange.start)) { setDateRange(null); return; }
            if (isBefore(date, dateRange.start)) { setDateRange({ start: date, end: null }); return; }

            const interval = eachDayOfInterval({ start: dateRange.start, end: date });
            const hasUnavailableDay = interval.some(d => !getDayAvailability(d).full);

            if (hasUnavailableDay) {
                toast.error('Some days in this range are unavailable');
                return;
            }

            setDateRange({ start: dateRange.start, end: date });
            setBookingType('full_day');
        }
    };

    const handleBooking = async () => {
        if (!dateRange?.start) return;

        const daysToBook = dateRange.end
            ? eachDayOfInterval({ start: dateRange.start, end: dateRange.end })
            : [dateRange.start];

        try {
            const promises = daysToBook.map(date =>
                createBooking.mutateAsync({
                    vendorId: vendor.id,
                    bookingDate: date,
                    bookingType,
                    totalPrice: calculation.perDay, // Price stored is per day (including guest count if applicable)
                    packageId: selectedPackageId || undefined,
                    userNote: userNote || undefined,
                    guestCount: selectedPackage?.is_per_guest ? guestCount : undefined
                })
            );

            await Promise.all(promises);
            toast.success(`Reserved ${daysToBook.length} days successfully!`);
            setDateRange(null);
            setSelectedPackageId('');
            setUserNote('');
        } catch (error) {
            console.error(error);
        }
    };

    // Render Calendar Grid
    const renderDays = () => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const days = [];
        const startDay = start.getDay();

        for (let i = 0; i < startDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-10 w-10" />);
        }

        for (let day = 1; day <= end.getDate(); day++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const avail = getDayAvailability(date);
            const isPast = isBefore(date, startOfToday());
            const isUnavailable = !avail.full && !avail.morning && !avail.evening;

            const isStart = dateRange?.start && isSameDay(date, dateRange.start);
            const isEnd = dateRange?.end && isSameDay(date, dateRange.end);
            const isInRange = dateRange?.start && dateRange?.end && isWithinInterval(date, { start: dateRange.start, end: dateRange.end });

            let cellClass = "h-10 w-10 text-sm rounded-full flex items-center justify-center transition-all relative ";

            if (isPast || isUnavailable) {
                cellClass += "text-gray-300 dark:text-gray-600 line-through cursor-not-allowed ";
            } else if (isStart || isEnd) {
                cellClass += "bg-rose-600 text-white font-semibold cursor-pointer shadow-lg z-10 ";
            } else if (isInRange) {
                cellClass += "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 ";
            } else {
                cellClass += "hover:bg-rose-50 dark:hover:bg-rose-900/30 text-gray-700 dark:text-gray-200 cursor-pointer ";
                if (!avail.full && (avail.morning || avail.evening)) {
                    cellClass += "border border-rose-200 dark:border-rose-800 ";
                }
            }

            // Capacity Indicator Dot
            const showCapacity = capacity > 1 && !isPast && !isUnavailable;
            // avail.remaining available here

            days.push(
                <button
                    key={day}
                    disabled={isPast || isUnavailable}
                    onClick={() => handleDateClick(date)}
                    className={cellClass}
                    title={showCapacity ? `${avail.remaining} slots available` : undefined}
                >
                    {day}
                    {showCapacity && avail.remaining !== undefined && avail.remaining < capacity && (
                        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                    )}
                </button>
            );
        }
        return days;
    };

    const bookingsCount = calculation.days;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden sticky top-24">
            {/* Header / Price */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 backdrop-blur-sm">
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        ₹{calculation.total > 0 ? calculation.total.toLocaleString() : 'Check Price'}
                    </span>
                    <span className="text-gray-500 text-sm font-medium">
                        {selectedPackage?.is_per_guest ? ' total (estimated)' : (bookingsCount > 1 ? ` for ${bookingsCount} days` : '/ day')}
                    </span>
                </div>
                {capacity > 1 && (
                    <div className="text-xs text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1 mt-1">
                        <Car size={12} />
                        Vendor has multiple units available
                    </div>
                )}
            </div>

            {/* Calendar */}
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {format(currentMonth, 'MMMM yyyy')}
                    </h3>
                    <div className="flex gap-2">
                        <button onClick={() => setCurrentMonth(prev => addMonths(prev, -1))} disabled={isSameMonth(currentMonth, new Date())} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full disabled:opacity-30 transition-colors">
                            <ChevronLeft size={20} />
                        </button>
                        <button onClick={() => setCurrentMonth(prev => addMonths(prev, 1))} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-y-2 mb-2 text-center">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                        <div key={d} className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-y-2 justify-items-center">
                    {renderDays()}
                </div>
            </div>

            {/* Selection Options */}
            {dateRange?.start && (
                <div className="px-6 pb-6 animate-in slide-in-from-top-4 fade-in duration-200 space-y-4">

                    {/* Packages Selection */}
                    {packages.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Package size={16} /> Select Package (Optional)
                            </label>
                            <select
                                value={selectedPackageId}
                                onChange={(e) => setSelectedPackageId(e.target.value)}
                                className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 transition-all text-sm"
                            >
                                <option value="">No Package (Base Rate)</option>
                                {packages.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} - ₹{p.price.toLocaleString()} {p.is_per_guest ? '/ guest' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Guest Count Input (Only if package is per_guest) */}
                    {selectedPackage?.is_per_guest && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Users size={16} /> Number of Guests
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={guestCount}
                                onChange={(e) => setGuestCount(parseInt(e.target.value) || 0)}
                                className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 transition-all text-sm"
                            />
                        </div>
                    )}

                    {!dateRange.end && capacity === 1 && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select Duration
                            </label>
                            {/* Slots only for single capacity vendors */}
                            <button
                                onClick={() => setBookingType('full_day')}
                                disabled={!getDayAvailability(dateRange.start).full}
                                className={`w-full p-3 rounded-lg border text-left transition-all ${bookingType === 'full_day'
                                    ? 'border-rose-600 bg-rose-50 dark:bg-rose-900/20 ring-1 ring-rose-600'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                    } ${!getDayAvailability(dateRange.start).full ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <span className="font-medium text-gray-900 dark:text-white">Full Day</span>
                            </button>

                            {isHalfDayEnabled && (
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <button onClick={() => setBookingType('morning')} disabled={!getDayAvailability(dateRange.start).morning} className={`p-3 rounded-lg border text-sm transition-all text-left ${bookingType === 'morning' ? 'border-rose-600 bg-rose-50 dark:bg-rose-900/20 ring-1 ring-rose-600' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'} ${!getDayAvailability(dateRange.start).morning ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`}>
                                        <div className="font-medium text-gray-900 dark:text-white mb-0.5">Morning</div>
                                        <div className="text-xs text-gray-500">{vendor.slot_morning_start?.slice(0, 5)} - {vendor.slot_morning_end?.slice(0, 5)}</div>
                                    </button>
                                    <button onClick={() => setBookingType('evening')} disabled={!getDayAvailability(dateRange.start).evening} className={`p-3 rounded-lg border text-sm transition-all text-left ${bookingType === 'evening' ? 'border-rose-600 bg-rose-50 dark:bg-rose-900/20 ring-1 ring-rose-600' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'} ${!getDayAvailability(dateRange.start).evening ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`}>
                                        <div className="font-medium text-gray-900 dark:text-white mb-0.5">Evening</div>
                                        <div className="text-xs text-gray-500">{vendor.slot_evening_start?.slice(0, 5)} - {vendor.slot_evening_end?.slice(0, 5)}</div>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* User Notes */}
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <MessageSquare size={16} /> Add a note (Optional)
                        </label>
                        <textarea
                            value={userNote}
                            onChange={(e) => setUserNote(e.target.value)}
                            placeholder="Describe your event details or special requests..."
                            className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 transition-all text-sm min-h-[80px]"
                        />
                    </div>
                </div>
            )}

            {/* Footer / CTA */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <button
                    onClick={handleBooking}
                    disabled={!dateRange?.start || createBooking.isPending || (selectedPackage?.is_per_guest && guestCount <= 0)}
                    className="w-full py-3.5 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-bold rounded-xl shadow-lg shadow-rose-200 dark:shadow-rose-900/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                    {createBooking.isPending ? 'Processing...' : `Reserve ${dateRange?.end ? `(${bookingsCount} days)` : ''}`}
                </button>
                <div className="text-center mt-3 text-xs text-gray-500 flex items-center justify-center gap-1">
                    <Info size={12} />
                    You won't be charged yet
                </div>
            </div>

            {/* T&C Hint */}
            {vendor.terms_and_conditions && (
                <div className="px-6 pb-4 text-xs text-gray-400 text-center">
                    By booking, you agree to the vendor's <button className="underline hover:text-gray-600">Terms & Conditions</button>
                </div>
            )}
        </div>
    );
}
